import {
  ChannelMessage,
  getTypedObjectKeys,
  InboxMessage,
  MessageRootEntity,
  ThreadUnreadData,
  UnreadThread,
} from '@schooly/api';
import { isToday, isYesterday } from 'date-fns';

import { convertApiTimestamp } from '../../utils/convertApiTimestamp';

export const TODAY = 'messages-ThreadChannelMessageToday';
export const YESTERDAY = 'messages-ThreadChannelMessageYesterday';
export const OLDER = 'messages-ThreadChannelMessageOlder';

export type ThreadNotification = {
  type: 'thread';
  date: Date | null;
  rootEntity: MessageRootEntity;
  unreadThreads: UnreadThread[];
  key: string;
};

export type MessageNotification = {
  type: 'message';
  date: Date | null;
  message: InboxMessage;
  key: string;
};

export type Notification = ThreadNotification | MessageNotification;

export type DateString = typeof TODAY | typeof YESTERDAY | typeof OLDER;
export type Notifications = Record<DateString, Notification[]>;

export const transformToThreadNotifications = (
  threadsUnreadData: ThreadUnreadData,
): ThreadNotification[] => {
  const notifications: Set<ThreadNotification> = new Set();

  const messageIds = getTypedObjectKeys(threadsUnreadData);

  for (const messageId of messageIds) {
    const threadUnreadData = threadsUnreadData[messageId];

    if (!threadUnreadData.length) continue;

    const lastChannelMessageDate = threadUnreadData.reduce(
      (acc, item) => {
        const date = item.last_channel_message?.created_at
          ? getNotificationDate(item.last_channel_message?.created_at)
          : null;

        if (!date) return acc;

        return !acc || date.getTime() > acc.getTime() ? date : acc;
      },
      null as Date | null,
    );

    notifications.add({
      type: 'thread',
      date: lastChannelMessageDate,
      rootEntity: threadUnreadData[0].root_entity,
      unreadThreads: threadUnreadData,
      key: `${messageId}-${threadUnreadData[0].thread_id}`,
    });
  }

  return Array.from(notifications);
};

export const transformToMessageNotifications = (
  messages: InboxMessage[],
): MessageNotification[] => {
  return messages.map((message) => ({
    type: 'message' as const,
    date: getNotificationDate(message.created_at),
    key: `${message.id}-${message.recipient_id}-${message.created_at}`,

    message,
  }));
};

const getNotificationDate = (date: InboxMessage['created_at'] | ChannelMessage['created_at']) => {
  if (!date) return null;
  return typeof date === 'number' ? new Date(Number(convertApiTimestamp(date))) : new Date(date);
};

export const sortNotificationsByDate = (notifications: Notification[]): Notifications => {
  const result = notifications.reduce<Notifications>(
    (acc, notification) => {
      if (!notification.date) return acc;

      if (isToday(notification.date)) {
        acc[TODAY].push(notification);
      } else if (isYesterday(notification.date)) {
        acc[YESTERDAY].push(notification);
      } else {
        acc[OLDER].push(notification);
      }

      return acc;
    },
    {
      [TODAY]: [],
      [YESTERDAY]: [],
      [OLDER]: [],
    },
  );

  const sortByDateDesc = (a: Notification, b: Notification) => {
    if (!a.date || !b.date) return 0;
    return b.date.getTime() - a.date.getTime();
  };

  return getTypedObjectKeys(result).reduce<Notifications>((acc, category) => {
    acc[category].sort(sortByDateDesc);
    return acc;
  }, result);
};
